import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    useTranslate,
    TopToolbar,
    TextInput,
    SelectInput,
    DateInput,
    ExportButton,
    ReferenceField,
    useNotify,
    useRefresh,
    useDataProvider,
    useListContext,
    RecordContextProvider,
    ShowButton,
} from 'react-admin';
import { Box, Chip, Typography, Button, useMediaQuery, useTheme } from '@mui/material';
import { Schedule, CheckCircle, Cancel, Pending } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';


const DesktopListActions = () => (
    <TopToolbar>
        <ExportButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const getStatusInfo = (record: any, translate: ReturnType<typeof useTranslate>) => {
    const now = new Date();
    const effectiveDate = new Date(record.effectiveDate);

    let status = record.status || 'pending';
    let icon = <Pending sx={{ fontSize: 16 }} />;
    let color: 'warning' | 'success' | 'error' | 'default' = 'warning';

    switch (status) {
        case 'cancelled':
            icon = <Cancel sx={{ fontSize: 16 }} />;
            color = 'error';
            break;
        case 'applied':
            icon = <CheckCircle sx={{ fontSize: 16 }} />;
            color = 'success';
            break;
        case 'pending':
            if (effectiveDate < now) {
                status = 'overdue';
                color = 'error';
            }
            break;
    }

    return { status, icon, color, label: translate(`resources.fee-template-changes.status.${status}`) };
};

const getChangesSummary = (record: any) => {
    if (!record.newStructure) return '';

    const changes: string[] = [];
    const components = record.newStructure.components || {};

    const enabledComponents = Object.entries(components)
        .filter(([key, value]: [string, any]) => value.enabled)
        .map(([key]) => key);

    if (enabledComponents.length > 0) {
        changes.push(`${enabledComponents.length} fee components`);
    }

    if (record.newStructure.currency) {
        changes.push(record.newStructure.currency);
    }

    return changes.join(' • ');
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();

    if (isLoading || !data) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {data.map((record) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/fee-template-changes/${record.id}/show`)}
                        sx={{
                            p: 2,
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            cursor: 'pointer',
                            '&:active': { backgroundColor: '#F5F5F5' },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    mr: 1,
                                }}
                            >
                                <ReferenceField source="templateId" reference="fee-templates" link={false}>
                                    <TextField source="name" />
                                </ReferenceField>
                            </Typography>
                            <Chip
                                label={getStatusInfo(record, translate).label}
                                color={getStatusInfo(record, translate).color}
                                size="small"
                                icon={getStatusInfo(record, translate).icon}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {record.effectiveDate
                                    ? new Date(record.effectiveDate).toLocaleDateString(undefined, {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                      })
                                    : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {getChangesSummary(record)}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

const useFeeTemplateChangesFilters = () => {
    const translate = useTranslate();

    return [
        <TextInput key="search" source="q" label={translate('resources.fee-template-changes.list.filters.search')} alwaysOn />,
        <SelectInput
            key="status"
            source="status"
            label={translate('resources.fee-template-changes.list.filters.status')}
            choices={[
                { id: 'pending', name: translate('resources.fee-template-changes.status.pending') },
                { id: 'applied', name: translate('resources.fee-template-changes.status.applied') },
                { id: 'cancelled', name: translate('resources.fee-template-changes.status.cancelled') },
            ]}
        />,
        <DateInput key="effectiveFrom" source="effectiveFrom" label={translate('resources.fee-template-changes.list.filters.effectiveFrom')} />,
        <DateInput key="effectiveTo" source="effectiveTo" label={translate('resources.fee-template-changes.list.filters.effectiveTo')} />,
    ];
};

export const FeeTemplateChangesList = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const feeTemplateChangesFilters = useFeeTemplateChangesFilters();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            title={translate('resources.fee-template-changes.name')}
            actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
            filters={feeTemplateChangesFilters}
            sort={{ field: 'effectiveDate', order: 'DESC' }}
            sx={{
                '& .RaList-content': {
                    backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                },
                ...(isMobile ? {
                    '& .RaList-main': { margin: 0 },
                    '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                    '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                    '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                    '& .RaListToolbar-root > span': { display: 'none' },
                    '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                    '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                    '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                    '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                    '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                    '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                } : {}),
            }}
            empty={
                <Box textAlign="center" m={4}>
                    <Schedule sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {translate('resources.fee-template-changes.empty.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {translate('resources.fee-template-changes.empty.subtitle')}
                    </Typography>
                </Box>
            }
        >
            {isMobile ? (
                <MobileList />
            ) : (
                <Datagrid
                    bulkActionButtons={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 'bold',
                        },
                        '& .RaDatagrid-row': {
                            '&:hover': {
                                backgroundColor: '#F0F7FF',
                            },
                        },
                    }}
                >
                    <ReferenceField
                        source="templateId"
                        reference="fee-templates"
                        label={translate('resources.fee-template-changes.fields.template')}
                    >
                        <TextField source="name" />
                    </ReferenceField>
                    <FunctionField
                        label={translate('resources.fee-template-changes.fields.changes')}
                        render={(record: any) => {
                            if (!record.newStructure) return null;

                            const changes = [];
                            const components = record.newStructure.components || {};

                            const enabledComponents = Object.entries(components)
                                .filter(([key, value]: [string, any]) => value.enabled)
                                .map(([key]) => key);

                            if (enabledComponents.length > 0) {
                                changes.push(`${enabledComponents.length} fee components`);
                            }

                            if (record.newStructure.currency) {
                                changes.push(record.newStructure.currency);
                            }

                            return (
                                <Typography variant="body2" component="span">
                                    {changes.join(' • ')}
                                </Typography>
                            );
                        }}
                    />
                    <DateField
                        source="effectiveDate"
                        label={translate('resources.fee-template-changes.fields.effectiveDate')}
                        showTime
                        options={{
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        }}
                    />
                    <FunctionField
                        label={translate('resources.fee-template-changes.fields.status')}
                        render={(record: any) => {
                            const { label, color, icon } = getStatusInfo(record, translate);

                            return (
                                <Chip
                                    label={label}
                                    color={color}
                                    size="small"
                                    icon={icon}
                                />
                            );
                        }}
                    />
                    <TextField
                        source="reason"
                        label={translate('resources.fee-template-changes.fields.reason')}
                    />
                    <DateField
                        source="createdAt"
                        label={translate('resources.fee-template-changes.fields.createdAt')}
                        showTime
                        options={{
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        }}
                    />
                    <FunctionField
                        label={translate('resources.fee-template-changes.fields.actions')}
                        textAlign="right"
                        render={(record: any) => {
                            if (record.status !== 'pending') {
                                return null;
                            }

                            const handleCancel = async () => {
                                try {
                                    await dataProvider.update('fee-template-changes', {
                                        id: record.id,
                                        data: { status: 'cancelled' },
                                        previousData: record,
                                    });

                                    notify('resources.fee-template-changes.notifications.cancelled', { type: 'success' });
                                    refresh();
                                } catch (error) {
                                    console.error('Cancel error:', error);
                                    notify('resources.fee-template-changes.notifications.cancelFailed', { type: 'error' });
                                }
                            };

                            return (
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={handleCancel}
                                >
                                    {translate('resources.fee-template-changes.actions.cancel')}
                                </Button>
                            );
                        }}
                    />
                </Datagrid>
            )}
        </List>
    );
};
